import React, { PureComponent } from 'react';
import { connect } from 'react-redux';

import { makeGetLabels, makeGetUserLabels } from '../selectors/labels';
import { makeGetFetchedStateWithoutId, makeGetLoadingStateWithoutId } from '../selectors/global';
import { fetchLabels } from '../actions/labels';

import { addTaskLabel, removeTaskLabel } from '../actions/tasks';
import { addDocLabel, removeDocLabel } from '../actions/docs';
import { addFileLabel, removeFileLabel } from '../actions/files';
import { addUserLabel, removeUserLabel } from '../actions/users';

import Popout from './popout';
import LabelSelector from './label-selector';
import LabelEditor from './label-editor';

class LabelPicker extends PureComponent { 
    static defaultProps = {
        xAlign: 'right',
        withEdit: true
    }

    state = {
        visible: false,
        editing: false,
        editingLabelId: null
    }

    handleToggleClick = () => {
        this.popoutCtrl && this.popoutCtrl.show();
    }

    handlePopouShow = () => {
        this.setState({
            visible: true
        });

        this.props.onShow && this.props.onShow();
    }

    handlePopoutHide = () => {
        this.setState({
            visible: false
        });

        this.props.onHide && this.props.onHide();
    }

    handleCreateClick = () => {
        this.setState({
            editing: true
        });
    }

    handleLabelEditClick = labelId => {
        this.setState({
            editing: true,
            editingLabelId: labelId
        });
    }

    handleLabelClick = labelId => {
        const { assignedLabels } = this.props;
        const remove = assignedLabels?.includes(labelId);

        if (remove) {
            this.unassignLabel(labelId);
        } else {
            this.assignLabel(labelId);
        }
    }

    handleClose = () => {
        this.popoutCtrl && this.popoutCtrl.hide();
    }

    handleEditCancel = () => {
        this.setState({
            editing: false,
            editingLabelId: null
        });
    }

    handleEditSuccess = () => {
        this.setState({
            editing: false,
            editingLabelId: null
        });
    }

    handleCreateSuccess = labelId => {
        this.assignLabel(labelId);
    }

    handleClearClick = () => {
        this.popoutCtrl && this.popoutCtrl.hide();

        this.props.onClearClick();
    }

    assignLabel(labelId) {
        if (this.props.onAdd) {
            this.props.onAdd(labelId);

            this.popoutCtrl.hide();
            return;
        }

        if (this.props.taskId) {
            addTaskLabel(this.props.taskId, labelId);
        } else if (this.props.docId) {
            addDocLabel(this.props.docId, labelId);
        } else if (this.props.fileId) {
            addFileLabel(this.props.fileId, labelId);
        } else if (this.props.userId) {
            addUserLabel(this.props.userId, labelId);
        }
    }

    unassignLabel(labelId) {
        if (this.props.onRemove) {
            this.props.onRemove(labelId);

            this.popoutCtrl.hide();
            return;
        }

        if (this.props.taskId) {
            removeTaskLabel(this.props.taskId, labelId);
        } else if (this.props.docId) {
            removeDocLabel(this.props.docId, labelId);
        } else if (this.props.fileId) {
            removeFileLabel(this.props.fileId, labelId);
        } else if (this.props.userId) {
            removeUserLabel(this.props.userId, labelId);
        }
    }

    attemptFetch() {
        if (this.props.fetched || this.props.loading || !this.state.visible) {
            return;
        }

        fetchLabels();
    }

    setPopoutCtrl = ctrl => this.popoutCtrl = ctrl;

    componentDidMount() {
        this.attemptFetch();
    }

    componentDidUpdate() {
        this.attemptFetch();
    }

    render() {
        const childrenWithProps = React.Children.map(this.props.children, child => {
            return React.cloneElement(child, {
                onClick: this.handleToggleClick
            });
        });

        return (
            <React.Fragment>
                { childrenWithProps }

                <Popout
                    className="xxsmall label-picker-popout no-arrow"
                    xAlign={this.props.xAlign}
                    defaultYPosition={this.props.defaultYPosition}
                    defaultXPosition={this.props.defaultXPosition}
                    resizeToFit={true}
                    minHeight={200}
                    inline={this.props.inline}
                    provideController={this.setPopoutCtrl}
                    onShow={this.handlePopouShow}
                    onHide={this.handlePopoutHide}
                >
                    <div className="label-picker">
                        {
                            this.state.editing ? (
                                <LabelEditor
                                    labelId={this.state.editingLabelId}
                                    type={this.props.userId ? 'member' : 'others'}
                                    onClose={this.handleClose}
                                    onCancel={this.handleEditCancel}
                                    onSuccess={this.handleEditSuccess}
                                    onCreate={this.handleCreateSuccess}
                                />
                            ) : (
                                <LabelSelector
                                    labels={this.props.labels}
                                    assignedLabels={this.props.assignedLabels}
                                    loading={this.props.loading}
                                    withEdit={this.props.withEdit}
                                    onLabelEditClick={this.handleLabelEditClick}
                                    onLabelClick={this.handleLabelClick}
                                    onClose={this.handleClose}
                                    onCreateClick={this.handleCreateClick}
                                    onClearClick={this.props.onClearClick && this.handleClearClick}
                                />
                            )
                        }
                    </div>
                </Popout>
            </React.Fragment>
        )
    }
}

const makeMapStateToProps = () => {
    const getLabels = makeGetLabels();
    const getUserLabels = makeGetUserLabels();
    const getLabelsLoading = makeGetFetchedStateWithoutId();
    const getLabelsFetched = makeGetLoadingStateWithoutId();

    return ({ labels }, ownProps) => {
        return {
            labels: ownProps.userId ? getUserLabels(labels.items) : getLabels(labels.items),
            loading: getLabelsLoading(labels.fetchInfo, 'team'),
            fetched: getLabelsFetched(labels.fetchInfo, 'team')
        }
    }
};

export default connect(makeMapStateToProps)(LabelPicker);
